import { GradientAvatar } from '@/shared/components';
import { useUserById } from '@/shared/hooks/use-user-by-id';
import { formatDateCompact } from '@/shared/utilities';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import type { MessageSearchItem } from '../../models/message-search-dto';
import { useConversationMembers } from '../../queries/member.queries';

interface MessageSearchResultItemProps {
  item: MessageSearchItem;
  searchTerm: string;
  onClick: (item: MessageSearchItem) => void;
}

/**
 * Highlights search term in text
 */
function highlightText(text: string, searchTerm: string): React.ReactNode {
  if (!searchTerm || searchTerm.length < 3) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <Box component='mark' key={`hiline-${part}-${i}`} sx={{ bgcolor: 'warning.light', px: 0.25, borderRadius: 0.5 }}>
        {part}
      </Box>
    ) : (
      part
    )
  );
}

export function MessageSearchResultItem({ item, searchTerm, onClick }: MessageSearchResultItemProps) {
  const theme = useTheme();
  const { data: members } = useConversationMembers(item.conversationId);
  const user = useUserById(members || [], item.senderId);

  const formattedDate = useMemo(() => {
    if (!item.createdAt) return '';
    return formatDateCompact(item.createdAt);
  }, [item.createdAt]);

  const highlightedContent = useMemo(() => highlightText(item.content, searchTerm), [item.content, searchTerm]);

  const handleClick = () => {
    onClick(item);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        borderRadius: 2,
        p: 1.5,
        gap: 1.5,
        transition: 'background-color 200ms',
        '&:hover': {
          backgroundColor: theme.vars.palette.action.hover
        }
      }}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <GradientAvatar
        peerId={item.senderId}
        name={user?.fullName}
        src={user?.profile.avatarRef || undefined}
        sx={{ width: 36, height: 36, flexShrink: 0 }}
      />

      <Stack direction='column' spacing={0.25} flex={1} minWidth={0}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='subtitle2' noWrap sx={{ fontWeight: 500 }}>
            {user?.fullName}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0, ml: 1 }}>
            {formattedDate}
          </Typography>
        </Box>

        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {highlightedContent}
        </Typography>
      </Stack>
    </Box>
  );
}
